// Global player search in the app header (⌘K). Results link to /players/{fpl_id}.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { searchPlayers } from "../../lib/lab-api";

const STATUS_BADGE = { i: "INJ", d: "DBT", s: "SUS", u: "OUT", n: "OUT" };

export default function GlobalPlayerSearch() {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [hi, setHi] = useState(0);
    const inputRef = useRef(null);
    const boxRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                inputRef.current?.focus();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    useEffect(() => {
        if (q.trim().length < 2) { setResults([]); setOpen(false); return; }
        const h = setTimeout(async () => {
            try {
                const d = await searchPlayers(q.trim(), 8);
                setResults(d.results || []);
                setOpen(true);
                setHi(0);
            } catch { /* ignore */ }
        }, 250);
        return () => clearTimeout(h);
    }, [q]);

    useEffect(() => {
        const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const go = (p) => {
        setOpen(false);
        setQ("");
        navigate(`/players/${p.fpl_id}`);
    };

    const onKeyDown = (e) => {
        if (!open || !results.length) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
        else if (e.key === "Enter") { e.preventDefault(); if (results[hi]) go(results[hi]); }
        else if (e.key === "Escape") setOpen(false);
    };

    return (
        <div className="relative hidden md:block" ref={boxRef} data-testid="global-player-search">
            <div className="flex items-center gap-1.5 border border-white/15 bg-black/40 px-2 py-1.5 focus-within:border-fa-mint/60 transition-colors">
                <MagnifyingGlass size={12} className="text-white/40" />
                <input
                    ref={inputRef}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={onKeyDown}
                    onFocus={() => { if (results.length) setOpen(true); }}
                    placeholder="Hledat hráče… ⌘K"
                    className="bg-transparent font-mono text-[11px] w-36 lg:w-44 focus:outline-none placeholder:text-white/30"
                    data-testid="global-search-input"
                />
            </div>
            {open && results.length > 0 && (
                <div className="absolute top-full right-0 mt-1 w-80 bg-[#0b0b0b] border border-fa-violet/40 shadow-2xl z-50 max-h-96 overflow-y-auto"
                     data-testid="global-search-results">
                    {results.map((p, i) => (
                        <button key={p.fpl_id} type="button" onClick={() => go(p)}
                                onMouseEnter={() => setHi(i)}
                                data-testid={`search-result-${p.fpl_id}`}
                                className={`w-full text-left px-3 py-2 flex items-center gap-2 border-b border-white/5 ${i === hi ? "bg-fa-violet/20" : ""}`}>
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-bold text-sm truncate">{p.name}
                                    {STATUS_BADGE[p.status] && (
                                        <span className="ml-1.5 font-mono text-[8px] px-1 py-0.5 bg-red-500/20 text-red-300">{STATUS_BADGE[p.status]}</span>
                                    )}
                                </div>
                                <div className="font-mono text-[9px] uppercase text-white/45 truncate">
                                    {p.team} · {p.position} · £{p.price?.toFixed?.(1) ?? p.price}
                                </div>
                            </div>
                            {p.in_pool && (
                                <span className="font-mono text-[8px] uppercase px-1.5 py-0.5 border border-fa-mint/40 text-fa-mint whitespace-nowrap">
                                    v poolu
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
